import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useState, useEffect} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {ELN_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import {AIModalSelectedFile} from 'notebook/ai-tools/AIModalFileChooser';
import AddFilesAndInstructionsStep, {
  getAddFilesAndInstructionsModalButtons,
} from 'notebook/ai-tools/flexible-data-entry/AddFilesAndInstructionsStep';
import {isCrossModelValidationVerified} from 'notebook/ai-tools/flexible-data-entry/CrossModelValidation';
import FlexibleDataEntryInfoBanner from 'notebook/ai-tools/flexible-data-entry/FlexibleDataEntryInfoBanner';
import {updateResultsWithEdits} from 'notebook/ai-tools/flexible-data-entry/FlexibleDataEntryResultEdits';
import ReviewAndConfirmResultsStep, {
  getReviewAndConfirmResultsModalButtons,
} from 'notebook/ai-tools/flexible-data-entry/ReviewAndConfirmResultsStep';
import {
  fillStructuredTableUsingAI,
  FillStructuredTableUsingAIFile,
  FillStructuredTableUsingAIResponse,
} from 'notebook/ai-tools/NotebookAIToolsDAO';
import {StructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import {Row} from 'notebook/tables/TableConstants';
import useIsNotUnmounted from 'ui-library/hooks/useIsNotUnmounted';
import {ExpandableModal} from 'ui-library/modals/ExpandableModal';
import {ModalButton} from 'ui-library/modals/Modal';
import StepNav, {StepNavVariant, StepNavSize, StepItemStatus} from 'ui-library/StepNav';
import * as ImportUtil from 'util/ImportUtil';
import {getUserMessage} from 'util/Net';

import {
  getUpdatedPromptTextWithSuggestedClarifications,
  useAIDataEntrySuggestedClarificationsControls,
} from './AIDataEntrySuggestedClarifications';
import {useFlexibleDataEntryResultEditsControls} from './FlexibleDataEntryResultEdits';

enum FlexibleDataEntryStep {
  ADD_FILES_AND_INSTRUCTIONS = 'ADD_FILES_AND_INSTRUCTIONS',
  REVIEW_AND_CONFIRM_RESULTS = 'REVIEW_AND_CONFIRM_RESULTS',
}

export interface AIFillStructuredTableModalResult {
  newRows: Array<Row>;
  fileS3Locations: Array<string>;
  promptText: string;
}

export interface AIFillStructuredTableModalProps {
  entryId: string;
  structuredTable: StructuredTable<unknown, unknown>;
  onRequestClose: () => void;
  onRequestSubmit: (result: AIFillStructuredTableModalResult) => void;
}

/**
 * Modal for the data entry assistant feature, allowing users to fill a structured table based on one
 * or more files or other context.
 *
 * This component is a multi-pane modal. All state management and analytics are handled by this layer,
 * and the visuals and interactions are handled by a child component/file per pane.
 */
export default function AIFillStructuredTableModal({
  entryId,
  structuredTable,
  onRequestSubmit,
  onRequestClose,
}: AIFillStructuredTableModalProps): JSX.Element {
  const isMounted = useIsNotUnmounted();
  const [currentStep, setCurrentStep] = useState<FlexibleDataEntryStep>(
    FlexibleDataEntryStep.ADD_FILES_AND_INSTRUCTIONS
  );

  // State specified by first step: add files and instructions.
  const [promptText, setPromptText] = useState<string>('');
  const [selectedFiles, setSelectedFiles] = useState<Array<AIModalSelectedFile>>([]);
  const {
    suggestedClarificationsState,
    setSuggestedClarificationsState,
    suggestClarifications,
    clearSuggestedClarifications,
  } = useAIDataEntrySuggestedClarificationsControls();

  const {modelEdits, handleCellEdit, clearModelEdits} = useFlexibleDataEntryResultEditsControls();

  // Server-provided state loaded by second step: review and confirm results.
  const [fillResult, setFillResult] = useState<FillStructuredTableUsingAIResponse | null>(null);
  const [selectedResultIndex, setSelectedResultIndex] = useState<number>(0);
  // Files that were passed into the request, for attaching if necessary.
  const [files, setFiles] = useState<Array<FillStructuredTableUsingAIFile> | null>(null);
  // Server-provided message when the whole operation fails.
  const [serverErrorMessage, setServerErrorMessage] = useState<string | null>(null);
  // Store the submitted prompt text to save after submission.
  const [submittedPromptText, setSubmittedPromptText] = useState<string | null>(null);

  const isSubmitted = currentStep === FlexibleDataEntryStep.REVIEW_AND_CONFIRM_RESULTS;
  const isCompleted = fillResult != null || serverErrorMessage != null;

  const selectedResult = fillResult?.runResults[selectedResultIndex];
  // true if the primary run configuration returned a nonempty response.
  const wasSuccessful = selectedResult?.newRows != null && selectedResult.newRows.length > 0;
  const wasVerified = wasSuccessful && fillResult != null && isCrossModelValidationVerified(fillResult);
  const numFiles = selectedFiles.length;

  useEffect(() => {
    trackEvent(ELN_ANALYTICS_ACTIONS.OPENED_FLEXIBLE_DATA_ENTRY_MODAL, {entryId});
  }, [entryId]);

  const handleSubmitPrompt = useCallback(async () => {
    if (isSubmitted) {
      return;
    }
    setCurrentStep(FlexibleDataEntryStep.REVIEW_AND_CONFIRM_RESULTS);
    const updatedPromptText = getUpdatedPromptTextWithSuggestedClarifications(
      promptText,
      structuredTable,
      suggestedClarificationsState
    );
    trackEvent(ELN_ANALYTICS_ACTIONS.SUBMITTED_FLEXIBLE_DATA_ENTRY_REQUEST, {numFiles, entryId});
    let files: Array<FillStructuredTableUsingAIFile>;
    let response;

    try {
      files = await Promise.all(
        selectedFiles.map(async ({file}) => ({
          s3Location: (await ImportUtil.upload(file))!,
          mediaType: file.type,
        }))
      );
      response = await fillStructuredTableUsingAI(entryId, structuredTable.name, files, updatedPromptText);
    } catch (e) {
      setServerErrorMessage(getUserMessage(e) ?? 'An error occurred.');
      throw e;
    }

    const responseWasSuccessful = response.runResults.some((result) => result.newRows?.length > 0);
    trackEvent(ELN_ANALYTICS_ACTIONS.RECEIVED_FLEXIBLE_DATA_ENTRY_RESULT, {
      isModalOpen: isMounted.current,
      wasSuccessful: responseWasSuccessful,
      wasVerified: isCrossModelValidationVerified(response),
      numFiles,
      entryId,
    });
    setSubmittedPromptText(updatedPromptText);
    setFillResult(response);
    setFiles(files);
  }, [
    entryId,
    isMounted,
    isSubmitted,
    numFiles,
    promptText,
    selectedFiles,
    structuredTable,
    suggestedClarificationsState,
  ]);

  const handleClose = useCallback(() => {
    trackEvent(ELN_ANALYTICS_ACTIONS.CLOSED_FLEXIBLE_DATA_ENTRY_MODAL, {
      isSubmitted,
      isCompleted,
      numFiles,
      entryId,
    });
    onRequestClose();
  }, [entryId, isCompleted, isSubmitted, numFiles, onRequestClose]);

  const handleSuggestClarifications = useCallback(
    (updatedFiles: Array<AIModalSelectedFile>) => {
      suggestClarifications(entryId, structuredTable.name, updatedFiles, promptText);
    },
    [entryId, structuredTable.name, promptText, suggestClarifications]
  );

  const handleInsert = useCallback(() => {
    if (!wasSuccessful || !selectedResult) {
      handleClose();
    } else {
      trackEvent(ELN_ANALYTICS_ACTIONS.ACCEPTED_FLEXIBLE_DATA_ENTRY_RESULT, {
        numFiles,
        wasVerified: isCrossModelValidationVerified(fillResult),
        entryId,
      });

      const editedRows = updateResultsWithEdits(selectedResult.newRows, modelEdits, selectedResultIndex);

      onRequestSubmit({
        newRows: editedRows,
        fileS3Locations: files!.map((f) => f.s3Location),
        promptText: submittedPromptText ?? '',
      });
    }
  }, [
    entryId,
    files,
    fillResult,
    handleClose,
    modelEdits,
    submittedPromptText,
    numFiles,
    onRequestSubmit,
    selectedResultIndex,
    selectedResult,
    wasSuccessful,
  ]);

  const handleStartOver = useCallback(() => {
    trackEvent(ELN_ANALYTICS_ACTIONS.STARTED_OVER_FLEXIBLE_DATA_ENTRY, {
      wasSuccessful,
      wasVerified,
      numFiles,
      entryId,
    });
    setCurrentStep(FlexibleDataEntryStep.ADD_FILES_AND_INSTRUCTIONS);
    setFillResult(null);
    setSelectedResultIndex(0);
    clearModelEdits();
    setFiles(null);
    setServerErrorMessage(null);
    setSubmittedPromptText(null);
  }, [entryId, numFiles, wasSuccessful, wasVerified, clearModelEdits]);

  let buttons: Array<ModalButton>;
  if (currentStep === FlexibleDataEntryStep.ADD_FILES_AND_INSTRUCTIONS) {
    buttons = getAddFilesAndInstructionsModalButtons(handleSubmitPrompt);
  } else {
    buttons = getReviewAndConfirmResultsModalButtons(
      wasVerified,
      wasSuccessful,
      isCompleted,
      handleStartOver,
      handleInsert
    );
  }

  return (
    <ExpandableModal
      title="Data entry assistant"
      large={true}
      onRequestClose={handleClose}
      buttons={buttons}
      closeableOnEscapeKey={false}
    >
      <FlexibleDataEntryInfoBanner />
      <div className={css(styles.stepNav)}>
        <StepNav<FlexibleDataEntryStep>
          steps={[
            {key: FlexibleDataEntryStep.ADD_FILES_AND_INSTRUCTIONS, title: 'Add files and instructions'},
            {key: FlexibleDataEntryStep.REVIEW_AND_CONFIRM_RESULTS, title: 'Review and confirm results'},
          ]}
          statusByStep={{
            [FlexibleDataEntryStep.ADD_FILES_AND_INSTRUCTIONS]:
              currentStep === FlexibleDataEntryStep.REVIEW_AND_CONFIRM_RESULTS
                ? StepItemStatus.DONE
                : StepItemStatus.UNVISITED,
            [FlexibleDataEntryStep.REVIEW_AND_CONFIRM_RESULTS]: StepItemStatus.UNVISITED,
          }}
          size={StepNavSize.SMALL}
          variant={StepNavVariant.FLAT}
          currentStepKey={currentStep}
          onUpdateCurrentStep={setCurrentStep}
          // Disable clicking to navigate since we want it to be clear that results are discarded.
          disabled={true}
        />
      </div>
      {currentStep === FlexibleDataEntryStep.ADD_FILES_AND_INSTRUCTIONS && (
        <AddFilesAndInstructionsStep
          selectedFiles={selectedFiles}
          onSetSelectedFiles={setSelectedFiles}
          promptText={promptText}
          onSetPromptText={setPromptText}
          suggestedClarificationsState={suggestedClarificationsState}
          structuredTable={structuredTable}
          onUpdateSuggestedClarificationsState={setSuggestedClarificationsState}
          onSuggestClarifications={handleSuggestClarifications}
          onClearSuggestedClarifications={clearSuggestedClarifications}
        />
      )}
      {currentStep === FlexibleDataEntryStep.REVIEW_AND_CONFIRM_RESULTS && (
        <ReviewAndConfirmResultsStep
          serverErrorMessage={serverErrorMessage}
          wasSuccessful={wasSuccessful}
          fillResult={fillResult}
          selectedResultIndex={selectedResultIndex}
          onSelectResultIndex={setSelectedResultIndex}
          structuredTable={structuredTable}
          modelEdits={modelEdits}
          onCellEdit={handleCellEdit}
        />
      )}
    </ExpandableModal>
  );
}

const styles = StyleSheet.create({
  stepNav: {
    marginTop: 15,
    marginBottom: 15,
  },
});
